/*
 * Modal
 * @module components
 * @flow
 */

import React from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components/macro';
import { ifProp } from 'styled-tools';
import { palette } from 'styled-theme';

import { Block } from '~/components/Block';
import { ClickAwayListener } from '~/components/ClickAwayListener';
// $FlowIssue
import { ReactComponent as IconClose } from '@/icons/ic-clear.svg';

export type ModalProps = {
  children?: React$Node,
  fullscreen?: boolean,
  isVisible?: boolean,
  isOverlay?: boolean,
  isClickAway?: boolean,
  onClose?: () => void,
};

const ModalWrapper = styled.div.attrs(() => ({
  testid: 'modal',
  role: 'dialog',
}))`
  display: flex;
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  padding: 15px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* NOTE: modal, search and tooltip must have the same z-index value but lower than notifications container */
  z-index: 999;
`;

const ModalCloseButton = styled(IconClose)`
  position: absolute;
  right: 15px;
  top: 15px;
  z-index: 10;
  cursor: pointer;
`;

const ModalOverlay = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
`;

const ModalContainer = styled(Block).attrs(() => ({
  elevation: 2,
}))`
  position: relative;
  display: flex;
  min-width: 317px;
  max-width: 100%;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  overflow: hidden;

  ${ifProp(
    'fullscreen',
    css`
      width: 100%;
      height: 100%;
    `,
  )};

  h4 {
    margin-bottom: 16px;
    font-size: 20px;
    font-weight: 600;
    color: ${palette('grayscale', 0)};
  }
`;

const ModalContent = styled.div`
  padding: 21px 15px 38px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow-y: auto;
`;

/**
 * Modal React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {React.Children} [props.children] - childs
 * @param {boolean} [props.fullscreen] - show modal in fullscreen mode
 * @param {boolean} [props.isVisible] - show modal state
 * @param {boolean} [props.isOverlay = true] - show overlay
 * @param {boolean} [props.isClickAway = true] - apply ClickAwayListener
 * @param {Function} [props.onClose = () => {}] - on close Modal callback
 * @param {string} [props.palette = 'primary'] - current palette
 * @example <caption>Simple usage</caption>
 * <Modal />
 * @returns {React.Node<*>} React-component
 */
export const Modal = ({
  children,
  fullscreen,
  isVisible,
  isOverlay,
  isClickAway,
  onClose,
}: ModalProps) => {
  const content = (
    <ModalContainer fullscreen={fullscreen}>
      <ModalCloseButton onClick={onClose} />
      <ModalContent>{children}</ModalContent>
    </ModalContainer>
  );

  return isVisible ? (
    <ModalWrapper testid="modal" key="modal">
      {isOverlay && <ModalOverlay />}
      {isClickAway ? (
        <ClickAwayListener onClickAway={onClose}>{content}</ClickAwayListener>
      ) : (
        content
      )}
    </ModalWrapper>
  ) : null;
};

Modal.defaultProps = {
  palette: 'primary',
  isOverlay: true,
  isClickAway: true,
};

Modal.Portal = ({ node, ...rest }) =>
  createPortal(<Modal {...rest} />, node || document.body);
